import axios from 'axios';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { transferShares } from 'src/api/stoApi';
import { Colors } from 'src/components/utils/Theme';
import { Asset } from 'src/models';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

interface Props {
  open: boolean;
  asset: Asset;
  handleClose: () => void;
  triggerRefresh: () => void;
}

export const TransferSharesDialog: FC<Props> = ({ open, asset, handleClose, triggerRefresh }) => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const [dialogState, setDialogState] = useState<'init' | 'error' | 'success'>('init');
  const [error, setError] = useState<string>('');

  let fundingFloorReached = false;

  if (asset.type === 'builderOwnerModel' || asset.type === 'powerplantPortfolio') {
    fundingFloorReached = true;
  } else if (asset.investmentDetails && asset.investmentDetails.issueFundingFloor) {
    const openBrickPercentage = ((asset.investmentDetails?.availableBricks ?? 1) / (asset.investmentDetails?.totalBricks ?? 1)) * 100;
    fundingFloorReached = 100 - openBrickPercentage >= (asset.investmentDetails?.issueFundingFloor ?? 0);
  }

  const initiateTransfer = async () => {
    setLoading(true);
    try {
      // Transfer Shares
      const transfer = await transferShares(asset.id); // here we should change something
      if (transfer.status === 200) {
        setDialogState('success');
        triggerRefresh();
      }
    } catch (error) {
      setDialogState('error');
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data.error);
        if (error.response.data.message) {
          setError(error.response.data?.message);
        }
        if (error.response.status === 412) {
          setError('Transfer Shares Denied');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAndReset = () => {
    handleClose();
    setDialogState('init');
    setError('');
  };

  return (
    <Dialog open={open} onClose={handleCloseAndReset}>
      {!fundingFloorReached && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.transfershares.fundingfloornotreached.headline')}</DialogTitle>
          <DialogContent sx={{ minWidth: 500 }}>
            <Typography>{t('dashboard.assets.dialog.transfershares.fundingfloornotreached.message')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.transfershares.fundingfloornotreached.ok')}
            </Button>
          </DialogActions>
        </>
      )}
      {fundingFloorReached && dialogState === 'init' && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.transfershares.init.headline')}</DialogTitle>
          <DialogContent sx={{ minWidth: 500 }}>
            <Typography>{t('dashboard.assets.dialog.transfershares.init.message')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.transfershares.init.cancel')}
            </Button>
            <LoadingButton loading={isLoading} variant="contained" disableElevation color="primary" onClick={initiateTransfer}>
              {t('dashboard.assets.dialog.transfershares.init.confirm')}
            </LoadingButton>
          </DialogActions>
        </>
      )}
      {fundingFloorReached && dialogState === 'error' && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.transfershares.error.headline')}</DialogTitle>
          <DialogContent sx={{ minWidth: 500 }}>
            <Typography>{t('dashboard.assets.dialog.transfershares.error.message')}</Typography>
            <Typography color={Colors.ERROR}>{error}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.transfershares.error.ok')}
            </Button>
          </DialogActions>
        </>
      )}
      {fundingFloorReached && dialogState === 'success' && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.transfershares.success.headline')}</DialogTitle>
          <DialogContent sx={{ minWidth: 500 }}>
            <Typography>{t('dashboard.assets.dialog.transfershares.success.message')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.transfershares.success.ok')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
